/* eslint-disable react/jsx-no-useless-fragment */
import { CategoryItemsProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
//import { GridComponent } from '../../../../../base/grid/Grid';
import { memo, useEffect, useState } from 'react';
import { getScaleSize, isBetweenRange, truncateText } from '../../../../../utils/utils';
import { TEMPLATE_5_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface Item extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

interface ItemProps {
  template: Template | null;
  item: Item;
  development?: boolean;
  lastItemIndex: number;
  index: number;
  timezone?: string;
  scale?: number;
  hasAnyModifier: boolean;
  fontFamily?: string;
  categoryId: number;
}

const Item = ({
  template,
  item,
  development,
  timezone,
  index,
  scale = 1,
  lastItemIndex,
  hasAnyModifier,
  fontFamily: fontFamilyProp,
  categoryId,
}: ItemProps) => {
  const [active, setActive] = useState<boolean | null>(true);
  const { normalizePixels } = useNormalizedPixels();

  useEffect(() => {
    if (!development) {
      const intervalId = setInterval(() => {
        setActive(isBetweenRange(item.schedules, timezone));
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, []);

  const isItemPriceEnabled = template?.itemPrice && template.itemPrice.isEnabled;
  const isDevelopment = development ? true : isBetweenRange(item.schedules, timezone);
  const truncatedDescription = truncateText(item?.itemDescription, 100);
  const fontFamily = fontFamilyProp || template?.fontFamilyName;
  const itemId = `item-${item.id}-category-${categoryId}`;

  return (
    active && (
      <>
        {isDevelopment && (
          <BoxComponent
            id={itemId}
            sx={{
              alignSelf: 'start',
              width: '48%',
              maxWidth: '100%',
              overflow: 'hidden',
              backgroundColor: 'transparent',
              borderRadius: 0,
              textTransform: 'capitalize',
              ...(lastItemIndex === index && isItemPriceEnabled
                ? {
                    marginTop: getScaleSize(normalizePixels(24, 'height'), scale),
                  }
                : {}),
            }}
          >
            <BoxComponent
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <BoxComponent
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  width: isItemPriceEnabled ? (item?.hasValidModifiers ? '50%' : '80%') : '100%',
                }}
              >
                <TypographyComponent
                  sx={{
                    fontWeight: template?.item.itemName.fontWeight,
                    fontStyle: template?.item.itemName.fontStyle,
                    letterSpacing: template?.item.itemName.letterSpacing,
                    lineHeight: template?.item.itemName.lineHeight,
                    fontFamily: fontFamily,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    '-webkit-line-clamp': '1',
                    '-webkit-box-orient': 'vertical',
                    color: template?.item?.itemName?.color ? template.item.itemName.color : '#242528',
                    fontSize: getScaleSize(
                      normalizePixels(25 * Number(template?.item?.textScale ?? 1), 'height'),
                      scale
                    ),
                    ...(item.outOfStock ? { opacity: '30%', textDecoration: 'line-through' } : {}),
                  }}
                >
                  {item.itemName}
                </TypographyComponent>
              </BoxComponent>

              {isItemPriceEnabled && item?.hasValidModifiers && (
                <BoxComponent width={'50%'} display={'flex'} justifyContent={'flex-end'}>
                  {item.hasValidModifiers && (
                    <GridComponent container sx={{ width: '100%' }}>
                      {TEMPLATE_5_MODIFIERS.map((size) => (
                        <GridComponent key={size} item size={6}>
                          <TypographyComponent
                            sx={{
                              textAlign: 'center',
                              fontFamily: fontFamily,
                              lineHeight: template.item.itemPrice.lineHeight,
                              fontWeight: template.item.itemPrice.fontWeight,
                              fontStyle: template.item.itemPrice.fontStyle,
                              letterSpacing: template.item.itemPrice.letterSpacing,
                              fontSize: getScaleSize(
                                normalizePixels(25 * Number(template?.item?.textScale ?? 1), 'height'),
                                scale
                              ),
                              color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#242528',
                              ...(item.outOfStock ? { opacity: '30%', textDecoration: 'line-through' } : {}),
                              height: '100%',
                              width: '100%',
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            {item.modifiers && item?.modifiers[size] ? (
                              Number(item?.modifiers[size]).toFixed(2)
                            ) : (
                              <div style={{ width: '10px', height: '1px', backgroundColor: 'black' }} />
                            )}
                          </TypographyComponent>
                        </GridComponent>
                      ))}
                    </GridComponent>
                  )}
                </BoxComponent>
              )}

              {(!hasAnyModifier || !item.hasValidModifiers) && isItemPriceEnabled && (
                <TypographyComponent
                  sx={{
                    textAlign: 'center',
                    fontFamily: fontFamily,
                    lineHeight: template.item.itemPrice.lineHeight,
                    fontWeight: template.item.itemPrice.fontWeight,
                    fontStyle: template.item.itemPrice.fontStyle,
                    letterSpacing: template.item.itemPrice.letterSpacing,
                    // width: '20%',
                    color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#242528',
                    fontSize: getScaleSize(
                      normalizePixels(25 * Number(template?.item?.textScale ?? 1), 'height'),
                      scale
                    ),
                    ...(item.outOfStock ? { opacity: '30%', textDecoration: 'line-through' } : {}),
                  }}
                >
                  {Number(item.itemPrice).toFixed(2)}
                </TypographyComponent>
              )}
            </BoxComponent>

            {template?.itemDescription && item?.itemDescription && template?.itemDescription.isEnabled && (
              <TypographyComponent
                sx={{
                  fontFamily: fontFamily,
                  fontWeight: template.item.itemDescription?.fontWeight,
                  fontStyle: template.item.itemDescription?.fontStyle,
                  letterSpacing: template.item.itemDescription?.letterSpacing,
                  lineHeight: template.item.itemDescription?.lineHeight,
                  display: '-webkit-box',
                  overflow: 'hidden',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  color: template?.item?.itemDescription?.color ? template.item.itemDescription.color : '#242528',
                  width: isItemPriceEnabled ? (item?.hasValidModifiers ? '55%' : '80%') : '100%',
                  fontSize: getScaleSize(normalizePixels(16 * Number(template?.item?.textScale ?? 1), 'height'), scale),
                  ...(item.outOfStock ? { opacity: '30%', textDecoration: 'line-through' } : {}),
                }}
              >
                {truncatedDescription}
              </TypographyComponent>
            )}
          </BoxComponent>
        )}
      </>
    )
  );
};

export default memo(Item);
